import { X, Plus, GripVertical, Search } from 'lucide-react';
import { useState } from 'react';
import { useApp } from '@/context/app-context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EXERCISE_LIBRARY } from '@/lib/exercise-library';
import { uid } from '@/lib/helpers';
import type { WorkoutExercise } from '@/lib/types';

type WorkoutType = 'Push' | 'Pull' | 'Legs' | 'Upper' | 'Lower' | 'Full Body' | 'Custom';

export function CreateWorkout() {
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workoutId = searchParams.get('id');

  const existingWorkout = workoutId ? state.workouts[workoutId] : null;
  const [name, setName] = useState(existingWorkout?.name || '');
  const [exercises, setExercises] = useState<WorkoutExercise[]>(existingWorkout?.exercises || []);
  const [workoutType, setWorkoutType] = useState<WorkoutType>('Push');
  const [notes, setNotes] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedExercise, setSelectedExercise] = useState<string>('');
  const [sets, setSets] = useState('4');
  const [reps, setReps] = useState('8');
  const [searchQuery, setSearchQuery] = useState('');
  const [showExercisePicker, setShowExercisePicker] = useState(false);

  const workoutTypes: WorkoutType[] = ['Push', 'Pull', 'Legs', 'Upper', 'Lower', 'Full Body', 'Custom'];

  const categories = Object.keys(EXERCISE_LIBRARY);
  const categoryExercises = selectedCategory ? EXERCISE_LIBRARY[selectedCategory] : [];

  // Filter exercises based on search query
  const filteredExercises = selectedCategory
    ? categoryExercises.filter((e) =>
        e.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const handleAddExercise = (exerciseName?: string) => {
    const targetName = exerciseName || selectedExercise;
    if (!targetName) return;
    const exercise = categoryExercises.find((e) => e.name === targetName);
    if (!exercise) return;

    const newExercise: WorkoutExercise = {
      exerciseId: exercise.name,
      name: exercise.name,
      sets: exercise.defaultSets,
      reps: exercise.defaultReps,
    };

    setExercises((prev) => [...prev, newExercise]);
    setSelectedExercise('');
    setSearchQuery('');
    setShowExercisePicker(false);
  };

  const handleRemoveExercise = (idx: number) => {
    setExercises(exercises.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (!name || exercises.length === 0) return;

    dispatch({
      type: 'UPSERT_WORKOUT',
      workout: {
        id: workoutId || uid(),
        name,
        exercises,
      },
    });

    navigate('/workouts');
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#18181B', width: '100%' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          onClick={() => navigate('/workouts')}
          style={{
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <X size={24} style={{ color: '#F0F0F0' }} />
        </button>

        <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
          {existingWorkout ? 'Edit Workout' : 'Create Workout'}
        </div>

        <div style={{ width: '44px' }} /> {/* Spacer for alignment */}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {/* Workout Name */}
        <div className="mb-6">
          <label
            style={{
              color: '#71717A',
              fontSize: '12px',
              fontWeight: 600,
              display: 'block',
              marginBottom: '8px',
              letterSpacing: '0.5px',
            }}
          >
            WORKOUT NAME
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Push Day A"
            className="w-full rounded-lg"
            style={{
              backgroundColor: '#27272A',
              color: '#F0F0F0',
              fontSize: '16px',
              fontWeight: 600,
              padding: '14px 16px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          />
        </div>

        {/* Workout Type */}
        <div className="mb-6">
          <label
            style={{
              color: '#71717A',
              fontSize: '12px',
              fontWeight: 600,
              display: 'block',
              marginBottom: '10px',
              letterSpacing: '0.5px',
            }}
          >
            WORKOUT TYPE
          </label>
          <div className="flex flex-wrap gap-2">
            {workoutTypes.map((type) => (
              <button
                key={type}
                onClick={() => setWorkoutType(type)}
                className="rounded-full px-4 py-2"
                style={{
                  backgroundColor: workoutType === type ? '#FF6B35' : '#27272A',
                  color: workoutType === type ? '#18181B' : '#A1A1AA',
                  fontSize: '13px',
                  fontWeight: 700,
                  border: workoutType === type ? '2px solid #FF6B35' : '2px solid transparent',
                  minHeight: '38px',
                }}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Exercises */}
        <div className="mb-6">
          <label
            style={{
              color: '#71717A',
              fontSize: '12px',
              fontWeight: 600,
              display: 'block',
              marginBottom: '10px',
              letterSpacing: '0.5px',
            }}
          >
            EXERCISES
          </label>

          {/* Exercise List */}
          <div className="space-y-2 mb-3">
            {exercises.map((exercise, index) => (
              <div
                key={index}
                className="p-3 rounded-lg flex items-center gap-3"
                style={{
                  backgroundColor: '#27272A',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {/* Drag Handle */}
                <button
                  style={{
                    color: '#71717A',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'grab',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                >
                  <GripVertical size={20} />
                </button>

                {/* Exercise Info */}
                <div className="flex-1">
                  <div
                    style={{
                      color: '#F0F0F0',
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '2px',
                    }}
                  >
                    {exercise.name}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="px-2 py-0.5 rounded"
                      style={{
                        backgroundColor: '#3F3F46',
                        color: '#A1A1AA',
                        fontSize: '11px',
                        fontWeight: 600,
                      }}
                    >
                      {exercise.sets} sets
                    </span>
                    <span style={{ color: '#71717A', fontSize: '12px' }}>
                      {exercise.reps} reps
                    </span>
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => handleRemoveExercise(index)}
                  style={{
                    minWidth: '32px',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#71717A',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>

          {/* Add Exercise Button */}
          <button
            onClick={() => setShowExercisePicker(true)}
            className="w-full rounded-lg flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'transparent',
              border: '2px dashed #52525b',
              color: '#71717A',
              padding: '14px',
              fontSize: '14px',
              fontWeight: 700,
              minHeight: '52px',
              cursor: 'pointer',
            }}
          >
            <Plus size={20} />
            Add Exercise
          </button>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label
            style={{
              color: '#71717A',
              fontSize: '12px',
              fontWeight: 600,
              display: 'block',
              marginBottom: '8px',
              letterSpacing: '0.5px',
            }}
          >
            NOTES (OPTIONAL)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this workout..."
            rows={4}
            className="w-full rounded-lg"
            style={{
              backgroundColor: '#27272A',
              color: '#F0F0F0',
              fontSize: '14px',
              padding: '12px 16px',
              border: '1px solid rgba(255,255,255,0.06)',
              resize: 'vertical',
            }}
          />
        </div>
      </div>

      {/* Bottom Save Button */}
      <div
        className="p-4"
        style={{
          backgroundColor: '#27272A',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <button
          onClick={handleSave}
          disabled={!name || exercises.length === 0}
          className="w-full rounded-xl flex items-center justify-center gap-2"
          style={{
            backgroundColor: !name || exercises.length === 0 ? '#71717A' : '#FF6B35',
            color: '#18181B',
            padding: '16px',
            fontSize: '16px',
            fontWeight: 700,
            minHeight: '56px',
            cursor: !name || exercises.length === 0 ? 'not-allowed' : 'pointer',
            border: 'none',
          }}
        >
          {existingWorkout ? 'Update' : 'Create'} Workout
        </button>
      </div>

      {/* Exercise Picker Modal */}
      {showExercisePicker && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => setShowExercisePicker(false)}
        >
          <div
            style={{
              backgroundColor: '#18181B',
              width: '100%',
              maxHeight: '70vh',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Picker Header */}
            <div
              className="p-4"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div
                  style={{ color: '#F0F0F0', fontSize: '18px', fontWeight: 700 }}
                >
                  Add Exercise
                </div>
                <button
                  onClick={() => setShowExercisePicker(false)}
                  style={{
                    minWidth: '32px',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <X size={20} style={{ color: '#71717A' }} />
                </button>
              </div>

              {/* Category Select */}
              <div className="mb-3">
                <label
                  style={{
                    color: '#71717A',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  CATEGORY
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setSelectedExercise('');
                    setSearchQuery('');
                  }}
                  className="w-full rounded-lg"
                  style={{
                    backgroundColor: '#27272A',
                    color: '#F0F0F0',
                    fontSize: '14px',
                    padding: '10px 12px',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search */}
              {selectedCategory && (
                <div className="relative">
                  <Search
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#71717A',
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search exercises..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-lg"
                    style={{
                      backgroundColor: '#27272A',
                      color: '#F0F0F0',
                      fontSize: '14px',
                      padding: '10px 12px 10px 36px',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  />
                </div>
              )}
            </div>

            {/* Exercise List */}
            <div className="overflow-y-auto flex-1 p-4">
              <div className="space-y-2">
                {selectedCategory && filteredExercises.length > 0 ? (
                  filteredExercises.map((exercise) => (
                    <button
                      key={exercise.name}
                      onClick={() => handleAddExercise(exercise.name)}
                      className="w-full p-3 rounded-lg text-left"
                      style={{
                        backgroundColor: '#27272A',
                        border: '1px solid rgba(255,255,255,0.06)',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        style={{
                          color: '#F0F0F0',
                          fontSize: '14px',
                          fontWeight: 600,
                          marginBottom: '2px',
                        }}
                      >
                        {exercise.name}
                      </div>
                      <div style={{ color: '#71717A', fontSize: '12px' }}>
                        {exercise.defaultSets}x{exercise.defaultReps}
                      </div>
                    </button>
                  ))
                ) : selectedCategory ? (
                  <div
                    style={{
                      color: '#71717A',
                      fontSize: '14px',
                      textAlign: 'center',
                      paddingTop: '20px',
                    }}
                  >
                    No exercises found
                  </div>
                ) : (
                  <div
                    style={{
                      color: '#71717A',
                      fontSize: '14px',
                      textAlign: 'center',
                      paddingTop: '20px',
                    }}
                  >
                    Select a category to see exercises
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
